"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { areaSlugs, coreAreaServices, manualAreaContent } from "../data/manualAreaContent";

const links = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/gallery", label: "Gallery" },
  { href: "/contactUs", label: "Contact" },
];

type MobileMenuProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const MobileMenu = ({ isOpen, setIsOpen }: MobileMenuProps) => {
  const pathname = usePathname();
  const [openSection, setOpenSection] = useState<"services" | "areas" | null>("services");

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen, setIsOpen]);

  const closeMenu = () => setIsOpen(false);

  return (
    <>
      <button onClick={() => setIsOpen((open) => !open)} className="grid size-11 place-items-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/16 md:hidden" aria-label={isOpen ? "Close menu" : "Open menu"} aria-expanded={isOpen} aria-controls="mobile-navigation-drawer">
        {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </button>

      <div onClick={closeMenu} className={`fixed inset-0 z-30 bg-[#082f49]/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${isOpen ? "visible opacity-100" : "invisible opacity-0"}`} />

      <aside id="mobile-navigation-drawer" className={`fixed right-0 top-0 z-40 flex h-full w-[90%] max-w-sm flex-col border-l border-[#0f172a]/10 bg-white shadow-2xl transition-transform duration-300 md:hidden ${isOpen ? "translate-x-0" : "translate-x-full"}`} aria-hidden={!isOpen}>
        <div className="flex items-center justify-between border-b border-[#0f172a]/10 px-5 py-5">
          <Link
            href="/"
            onClick={closeMenu}
            aria-label="Servani Safety Nets home"
            className="relative h-[46px] w-[151px] shrink-0 overflow-hidden rounded-xl border border-[#bae6fd] bg-white shadow-sm"
          >
            <Image
              src="/servani-safety-nets-logo.webp"
              alt="Servani Safety Nets"
              width={720}
              height={220}
              sizes="151px"
              className="h-full w-full object-contain"
            />
          </Link>
          <button onClick={closeMenu} className="grid size-10 place-items-center rounded-full bg-[#f0f9ff] text-[#0c4a6e]" aria-label="Close menu"><X className="size-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-28 pt-5">
          <nav aria-label="Mobile navigation">
            <div className="grid grid-cols-2 gap-2">
              {links.map((link) => (
                <Link key={link.href} href={link.href} onClick={closeMenu} className={`rounded-xl px-4 py-3 text-sm font-bold transition ${pathname === link.href ? "bg-[#0c4a6e] text-white" : "bg-[#f0f9ff] text-[#475569] hover:bg-[#e0f2fe] hover:text-[#0c4a6e]"}`}>
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="mt-7 space-y-3">
              <div className="overflow-hidden rounded-xl border border-[#0f172a]/10">
                <button onClick={() => setOpenSection(openSection === "services" ? null : "services")} className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-extrabold text-[#0c4a6e]" aria-expanded={openSection === "services"}>
                  <span>Core services</span><ChevronDown className={`size-4 transition ${openSection === "services" ? "rotate-180" : ""}`} />
                </button>
                <div className={openSection === "services" ? "max-h-80 overflow-y-auto border-t border-[#0f172a]/8 bg-[#f0f9ff] p-2" : "hidden"}>
                  {coreAreaServices.map((service) => (
                    <Link key={service.slug} href={`/services/${service.slug}`} onClick={closeMenu} className="block rounded-lg px-3 py-2.5 text-xs font-semibold leading-5 text-[#475569] hover:bg-[#e0f2fe] hover:text-[#0c4a6e]">{service.title}</Link>
                  ))}
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-[#0f172a]/10">
                <button onClick={() => setOpenSection(openSection === "areas" ? null : "areas")} className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-extrabold text-[#0c4a6e]" aria-expanded={openSection === "areas"}>
                  <span>Bengaluru neighbourhoods</span><ChevronDown className={`size-4 transition ${openSection === "areas" ? "rotate-180" : ""}`} />
                </button>
                <div className={openSection === "areas" ? "max-h-80 overflow-y-auto border-t border-[#0f172a]/8 bg-[#f0f9ff] p-2" : "hidden"}>
                  {areaSlugs.map((area) => (
                    <Link key={area} href={`/bangalore/${area}`} onClick={closeMenu} className="block rounded-lg px-3 py-2.5 text-xs font-semibold leading-5 text-[#475569] hover:bg-[#e0f2fe] hover:text-[#0c4a6e]">{manualAreaContent[area].name}</Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        </div>

        <div className="absolute inset-x-0 bottom-0 border-t border-[#0f172a]/10 bg-white/95 p-4 backdrop-blur">
          <a href="tel:+917995792953" className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#0369a1] px-5 py-3 text-sm font-extrabold text-white transition hover:bg-[#075985]"><Phone className="size-4" />Call for a free quote</a>
        </div>
      </aside>
    </>
  );
};

export default MobileMenu;
